import {
  hasI18nUserLanguageChoice,
  readEdgeLanguageRaw,
} from "@/lib/i18nEdgeSync";
import {
  readLangQueryParam,
  readPersistedUserLanguage,
  resolveQueryLangForDetection,
} from "@/lib/languagePolicy";
import { normalizeLanguageCode } from "@/lib/languages";
import { getServerTemplateConfig } from "@/lib/serverTemplateDetection";
import { usesSpanishDefaultForSite } from "@/lib/siteGeoPolicy";
import type { CustomDetector } from "i18next-browser-languagedetector";

/**
 * Orden en i18n/config: querystringLang → cloudflareEdge (Worker) → userExplicit → persistido → spanishDefaultGeo (fallback) → navigator.
 * El Worker (window.__TEMPLATE_CONFIG__ / cookie user_language) es la fuente autoritativa.
 * spanishDefaultGeo solo se aplica si el Worker no inyectó nada (dev local, Worker down).
 */

export const querystringLanguageDetector: CustomDetector = {
  name: "querystringLang",
  lookup() {
    const raw = readLangQueryParam();
    if (!raw) return undefined;
    return resolveQueryLangForDetection(raw);
  },
};

/**
 * Fallback español cuando NO hay Worker (dev local, Worker caído).
 * Si el Worker inyectó language, cloudflareEdge corre antes y este detector no se llega a evaluar.
 */
export const spanishDefaultGeoDetector: CustomDetector = {
  name: "spanishDefaultGeo",
  lookup() {
    if (typeof window === "undefined") return undefined;
    // Worker presente → no pisamos; cloudflareEdge ya devolvió la respuesta.
    if (getServerTemplateConfig()) return undefined;
    return usesSpanishDefaultForSite() ? "es" : undefined;
  },
};

export const userExplicitLanguageDetector: CustomDetector = {
  name: "userExplicitLanguage",
  lookup() {
    if (typeof window === "undefined") return undefined;
    if (usesSpanishDefaultForSite()) return undefined;
    if (!hasI18nUserLanguageChoice()) return undefined;
    const raw = readPersistedUserLanguage();
    if (!raw) return undefined;
    return normalizeLanguageCode(raw);
  },
};

/** Last session locale (same key as html lang); runs after edge and explicit choice. */
export const persistedUserLanguageDetector: CustomDetector = {
  name: "persistedUserLanguage",
  lookup() {
    if (typeof window === "undefined") return undefined;
    const raw = readPersistedUserLanguage();
    if (!raw) return undefined;
    return normalizeLanguageCode(raw);
  },
};

// Worker = autoridad. No re-derivamos geo: lo que diga __TEMPLATE_CONFIG__.language
// (o la cookie user_language que él mismo setea) es la verdad. Si Worker no inyectó nada,
// caemos a los detectores siguientes en config.ts.
export const cloudflareEdgeLanguageDetector: CustomDetector = {
  name: "cloudflareEdge",
  lookup() {
    if (typeof window === "undefined") return undefined;
    const raw = readEdgeLanguageRaw();
    if (!raw) return undefined;
    return normalizeLanguageCode(raw.replace(/_/g, "-"));
  },
};
